import React from 'react';
import { AlertTriangle, X, DollarSign } from 'lucide-react';
import { Button } from '../ui/Button';
import type { ShopifyOrder } from '../../types/shopify';
import { formatCurrency } from '../../types/shopify';

interface PartialRefundWarningModalProps {
  order: ShopifyOrder;
  actionType: 'ready' | 'finish';
  onConfirm: () => void;
  onCancel: () => void;
}

const PartialRefundWarningModal: React.FC<PartialRefundWarningModalProps> = ({
  order,
  actionType,
  onConfirm,
  onCancel,
}) => {
  const refundedAmount = order.total_refunded || 0;
  const remainingAmount = order.total_price - refundedAmount;
  const hasRefundData = refundedAmount > 0;

  const actionLabel = actionType === 'ready' ? 'Mark as Ready' : 'Finish Order';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                Partially Refunded Order
              </h2>
              <p className="text-sm text-white/80">
                Please review before proceeding
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="p-5">
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Order</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {order.order_number}
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Customer</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {order.customer_name || 'Unknown'}
              </span>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 my-3" />
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Original Total</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(order.total_price, order.currency)}
              </span>
            </div>
            {hasRefundData ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Refunded</span>
                  <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                    -{formatCurrency(refundedAmount, order.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Remaining</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(remainingAmount, order.currency)}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                <DollarSign className="h-4 w-4 flex-shrink-0" />
                <span>Exact refund amount not available -- please verify in Shopify.</span>
              </div>
            )}
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
                  This order has been partially refunded
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {actionType === 'ready'
                    ? 'Marking this order as ready will allow it to be shipped and fulfilled. Please make sure this is intended before proceeding.'
                    : 'Proceeding will generate a shipping label and fulfill this order in Shopify. Please verify the order details are correct before continuing.'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 border-0"
            >
              I Understood, {actionLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartialRefundWarningModal;
